import type { Stripe } from "stripe";

export interface GetPayouts {
  docs: Payout[];
}

export const allPayoutStatuses = [
  "new",
  "succeeded",
  "card_declined",
  "resource_missing",
  "failed",
  "pending",
] as const;
export type PayoutStatus = (typeof allPayoutStatuses)[number];
export const PayoutStatus = allPayoutStatuses;

/**
 * Statuses where a charge was attempted and did not collect. Excludes "new"
 * (no charge yet / in-flight) and "pending" (e.g. ACH still settling), which
 * are not collection failures.
 */
export const payoutBillingFailureStatuses: readonly PayoutStatus[] = [
  "failed",
  "card_declined",
  "resource_missing",
];

export function isPayoutBillingFailure(status: PayoutStatus): boolean {
  return payoutBillingFailureStatuses.includes(status);
}

export interface RetryAttempt {
  status: PayoutStatus;
  createdAt: string;
  updatedAt: string;
}

interface LegacyCard {
  legacyCard: Stripe.Card;
}
export type StripePaymentMethodType = "card" | "us_bank_account";

export const ALLOWED_PAYMENT_METHOD_TYPES: readonly StripePaymentMethodType[] =
  ["card", "us_bank_account"] as const;

/** @see Stripe.PaymentMethod */
export type StripePaymentMethod = Stripe.PaymentMethod &
  (
    | { type: "card"; card: Stripe.PaymentMethod.Card }
    | {
        type: "us_bank_account";
        us_bank_account: Stripe.PaymentMethod.UsBankAccount;
      }
  );

export type PaymentMethod = LegacyCard | StripePaymentMethod;

export function isLegacyCard(
  paymentMethod: PaymentMethod,
): paymentMethod is LegacyCard {
  return "legacyCard" in paymentMethod;
}
export function isStripePaymentMethod(
  paymentMethod: PaymentMethod | null | undefined,
): paymentMethod is StripePaymentMethod {
  return !!paymentMethod && "id" in paymentMethod && !!paymentMethod.id;
}

export interface ChargeAttemptCardDetails {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

export interface ChargeAttemptBankAccountDetails {
  bank_name: string | null;
  last4: string | null;
  account_type: string | null;
}

/**
 * Display-only projection of the payment method used for a charge attempt. The
 * legacy Mongo path stores the full Stripe object on `payment_method`; the
 * unified CRDB reconstruction has only the persisted columns, so it populates
 * this narrow shape instead. Mirrors the merchant billing UI's `PaymentDetails`.
 */
export type ChargeAttemptPaymentMethod =
  | { type: "card"; card: ChargeAttemptCardDetails }
  | {
      type: "us_bank_account";
      us_bank_account: ChargeAttemptBankAccountDetails;
    };

export interface ChargeAttempt {
  _id: string;
  charge_id?: string;
  status?: string;
  payment_method?: PaymentMethod;
  /** Set by the unified CRDB reader; preferred over `payment_method` for display. */
  paymentMethodDetails?: ChargeAttemptPaymentMethod;
  webhook_event?: { failure_message?: string };
  retrievedStripeObject?: { message?: string };
  createdAt: string;
  updatedAt: string;
}

export interface Payout {
  _id: string;
  team: string;
  charge_id: string;
  status: PayoutStatus;
  charge: { currency: string; price: string };
  charge_attempts?: ChargeAttempt[];
  retryAttempts?: RetryAttempt[];
  createdAt: string;
  updatedAt: string;
  /** ISO string of the billing period start (CRDB teams only; absent for legacy Mongo payouts). */
  billingPeriodStart?: string;
}

export type GetPayoutOrders = PayoutOrder[];

interface Currency {
  currency: string;
  price: string;
}

export interface ChargeStruckValue {
  amount: number;
  currency: string;
  source?: string;
  asOf?: number;
}

export interface PayoutOrder {
  _id: string;
  charge: Currency;
  struck?: Partial<Record<string, ChargeStruckValue>> | null;
  order: { id?: number; number?: number; name?: string };
  created: string;
  customer: { email: string; first_name: string; last_name: string };
  damaged?: boolean;
  wrongProduct?: boolean;
  type:
    | string
    | {
        description: string;
        longDescription: string;
        expense: boolean | "variable";
        sortID: number;
      };
  externalNote?: string;
  fulfillmentGroupId?: string;
  /** Reason supplied by an admin when creating a manual payout order. */
  reason?: string;
  /** Email of the admin who created a manual payout order. */
  createdByEmail?: string;
}

export interface LegendEntry {
  description: string;
  expense: boolean | "variable";
  longDescription: string;
}

function formatExpense(expense: boolean | "variable" | undefined): string {
  if (expense === "variable") {
    return "TRUE or FALSE";
  }
  return expense ? "TRUE" : "FALSE";
}

/**
 * Extracts unique legend entries from payout orders.
 * Each entry contains the charge type description, expense flag, and long description.
 * Entries are sorted alphabetically by description.
 */
export function extractLegendEntries(orders: PayoutOrder[]): LegendEntry[] {
  const seenDescriptions = new Set<string>();
  const legendEntries: LegendEntry[] = [];

  for (const record of orders) {
    const description =
      typeof record.type === "string"
        ? record.type
        : record.type?.description || "";

    if (description && !seenDescriptions.has(description)) {
      seenDescriptions.add(description);

      const longDescription =
        typeof record.type === "object"
          ? record.type?.longDescription || ""
          : "";
      const expense =
        typeof record.type === "object"
          ? (record.type?.expense ?? false)
          : false;

      legendEntries.push({ description, expense, longDescription });
    }
  }

  legendEntries.sort((a, b) => a.description.localeCompare(b.description));

  return legendEntries;
}

/**
 * Generates legend CSV rows from payout orders.
 * Returns an array of string arrays representing CSV rows (including header).
 */
export function generateLegendRows(orders: PayoutOrder[]): string[][] {
  const rows: string[][] = [["Type", "Expense", "Description"]];
  const entries = extractLegendEntries(orders);

  for (const entry of entries) {
    rows.push([
      entry.description,
      formatExpense(entry.expense),
      entry.longDescription,
    ]);
  }

  return rows;
}
